// src/context/AuthContext.js
import React, { createContext, useEffect, useState, useContext } from "react";
import { auth, db, googleProvider, messagingPromise } from "../firebase"; // <-- Agregamos messagingPromise
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { doc, getDoc, getDocFromServer, setDoc, updateDoc, serverTimestamp } from "firebase/firestore"; // <-- Agregamos updateDoc
import { getToken } from "firebase/messaging"; // <-- Importamos getToken para las notificaciones
import { Capacitor } from "@capacitor/core"; // Para detectar si corremos en la app nativa

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Devuelve los roles de un objeto (perfil normalizado O doc crudo de usuario)
// como array de strings en minúsculas. Soporta:
//   - profile.roles (array ya normalizado)
//   - rol/role como STRING ("encargado") o como ARRAY (["obras","administracion"])
export function rolesDe(obj) {
  const raw = obj?.roles ?? obj?.rol ?? obj?.role ?? [];
  return (Array.isArray(raw) ? raw : [raw])
    .map((r) => String(r || "").toLowerCase().trim())
    .filter(Boolean);
}

// Helper multi-rol: ¿el objeto tiene alguno de los roles indicados?
// Sirve tanto para el perfil logueado como para docs crudos de la colección users.
export function hasRol(obj, ...names) {
  const wanted = names.map((n) => String(n || "").toLowerCase());
  return rolesDe(obj).some((r) => wanted.includes(r));
}

// ── Permisos con delegación por licencia ────────────────────────────────────
// `deleg` viene del hook useDelegaciones (config/app.delegaciones). La delegación
// se suma al rol propio; el corte real lo hacen igual las reglas de Firestore.
//   · Facturar: administracion/admin siempre; ventas si está delegado.
//   · Despachar: ventas/admin siempre; administracion si está delegado.
export function puedeFacturar(profile, deleg) {
  if (hasRol(profile, "admin", "administracion")) return true;
  return hasRol(profile, "ventas") && !!deleg?.ventasFactura;
}
export function puedeDespachar(profile, deleg) {
  if (hasRol(profile, "admin", "ventas")) return true;
  return hasRol(profile, "administracion") && !!deleg?.administracionDespacha;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       
  const [profile, setProfile] = useState(null); 
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const ref = doc(db, "users", u.uid);
        let snap = await getDoc(ref);

        // Con la caché persistente de Firestore, en el arranque (sobre todo en la
        // app nativa, que tiene un instante sin red al abrir) un getDoc puede
        // resolver contra una caché vacía y devolver "no existe" aunque el doc SÍ
        // exista en el servidor. Si eso pasara, crearíamos un perfil "cliente" por
        // error y mandaríamos al operario/encargado a la vista de clientes.
        // Antes de dar por NUEVO al usuario, confirmamos contra el servidor.
        if (!snap.exists()) {
          try {
            snap = await getDocFromServer(ref);
          } catch (e) {
            // Sin conexión no podemos confirmar. NO auto-creamos un perfil cliente
            // (sería destructivo y mostraría la vista equivocada); reintentará en el
            // próximo arranque, cuando haya red.
            console.warn("[auth] no se pudo confirmar el perfil contra el servidor:", e?.code || e);
            setProfile(null);
            setLoading(false);
            return;
          }
        }

        let perfilActual; // Variable para saber qué perfil usar en este momento

        if (snap.exists()) {
          const data = snap.data();
          // Normalización de roles. Soporta:
          //  - "role" (campo viejo, string) y "rol" (nuevo)
          //  - rol como STRING ("encargado") o como ARRAY (["obras","administracion"])
          // Exponemos SIEMPRE:
          //  - profile.roles: array de roles en minúsculas (para multi-rol)
          //  - profile.rol: string primario (roles[0]) → mantiene compatibilidad con
          //    todo el código que hace (profile.rol||"").toLowerCase()
          const rolRaw = data.rol ?? data.role ?? "cliente";
          const roles = (Array.isArray(rolRaw) ? rolRaw : [rolRaw])
            .map((r) => String(r || "").toLowerCase().trim())
            .filter(Boolean);
          const rolesFinal = roles.length ? roles : ["cliente"];
          perfilActual = { id: u.uid, ...data, roles: rolesFinal, rol: rolesFinal[0] };
          setProfile(perfilActual);
        } else {
          const basic = {
            id: u.uid,
            email: u.email,
            nombre: u.displayName || "Cliente Nuevo",
            rol: "cliente",             
            finnegansClienteCodigo: "",  
            habilitado: false,           
            createdAt: serverTimestamp(),
          };
          await setDoc(ref, basic);
          perfilActual = basic;
          setProfile(basic);
          console.log("Nuevo perfil B2B creado para:", u.email);
        }

        // =========================================================
        // 🔥 PASO 1 NOTIFICACIONES: Pedir permiso y guardar TOKEN
        // En la app NATIVA el token lo maneja Capacitor (src/native/push.js) y se
        // guarda en el mismo campo fcmToken; NO corremos el registro FCM web para
        // no pisar el token nativo.
        // =========================================================
        try {
          const messaging = Capacitor.isNativePlatform() ? null : await messagingPromise;
          if (messaging) {
            const currentToken = await getToken(messaging, { vapidKey: process.env.REACT_APP_FCM_VAPID_KEY });
            
            if (currentToken) {
              // Chequeamos si el token es nuevo o cambió, para no guardar en la base de datos sin sentido
              if (perfilActual.fcmToken !== currentToken) {
                  await updateDoc(ref, { fcmToken: currentToken });
                  console.log("✅ Token de notificaciones registrado y guardado con éxito.");
              }
            } else {
              console.log("⚠️ No se generó el Token. El usuario seguramente bloqueó las notificaciones.");
            }
          }
        } catch (err) {
          console.log("❌ Error al procesar las notificaciones push:", err);
        }

      } else {
        setProfile(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const logout = () => signOut(auth);

  const value = {
    user,
    profile,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}